import { twMerge } from 'tailwind-merge';
import { Pencil, Trash2, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { formatCurrency, formatQuantity } from '../../lib/format';
import { earningKinds, investmentTypes } from '../../types';
import type { Earning, InvestmentAsset, Trade } from '../../types';
import type { Position } from '../../lib/investments';
import type { ReactNode } from 'react';

function Money({ value, signed }: { value: number; signed?: boolean }) {
  const tone =
    !signed || value === 0
      ? 'text-foreground'
      : value > 0
        ? 'text-green-600'
        : 'text-red-600';
  const prefix = signed && value > 0 ? '+' : signed && value < 0 ? '-' : '';
  return (
    <span className={tone}>
      {prefix}R$ {formatCurrency(Math.abs(value))}
    </span>
  );
}

function TypeBadge({ asset }: { asset: InvestmentAsset }) {
  const meta = investmentTypes[asset.type];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="size-2 shrink-0 rounded-sm"
        style={{ backgroundColor: meta.color }}
        aria-hidden
      />
      <span className="text-xs text-muted-foreground">{meta.shortLabel}</span>
    </span>
  );
}

function RowActions({
  onEdit,
  onDelete,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          title="Editar"
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Pencil className="size-3.5" />
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          title="Excluir"
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/** Wrapper com scroll horizontal próprio — o body da página nunca rola lateralmente. */
export function TableShell({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
      <table className="w-full min-w-[640px] border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = 'left',
  className = '',
}: {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      className={twMerge(
        'whitespace-nowrap border-b border-border pb-2 pr-4 text-xs font-medium text-muted-foreground last:pr-0',
        alignClass,
        className
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  className = '',
  colSpan,
  title,
}: {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  colSpan?: number;
  /** Nome completo quando o texto da célula é cortado por ellipsis. */
  title?: string;
}) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <td
      colSpan={colSpan}
      title={title}
      className={twMerge('whitespace-nowrap border-b border-border/60 py-2.5 pr-4 last:pr-0', alignClass, className)}
    >
      {children}
    </td>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <p className="py-8 text-center text-sm text-muted-foreground">{children}</p>
  );
}

// ============================================
// CARTEIRA
// ============================================

export interface PortfolioTableProps {
  positions: Position[];
  /** Proventos recebidos por ativo, para a coluna de proventos. */
  earningsByAsset: Map<string, number>;
  totalMarketValue: number;
  onEditAsset: (asset: InvestmentAsset) => void;
  onDeleteAsset: (asset: InvestmentAsset) => void;
}

export function PortfolioTable({
  positions,
  earningsByAsset,
  totalMarketValue,
  onEditAsset,
  onDeleteAsset,
}: PortfolioTableProps) {
  if (positions.length === 0) {
    return <EmptyState>Nenhuma posição em aberto. Registre uma compra para começar.</EmptyState>;
  }

  const sorted = [...positions].sort(
    (a, b) => (b.marketValue ?? b.totalCost) - (a.marketValue ?? a.totalCost)
  );

  const totals = sorted.reduce(
    (acc, p) => ({
      cost: acc.cost + p.totalCost,
      market: acc.market + (p.marketValue ?? p.totalCost),
      earnings: acc.earnings + (earningsByAsset.get(p.asset.id) ?? 0),
    }),
    { cost: 0, market: 0, earnings: 0 }
  );

  return (
    <TableShell>
      <thead>
        <tr>
          <Th className="max-w-[9rem]">Papel</Th>
          <Th align="right">Qtd</Th>
          <Th align="right">Custo médio</Th>
          <Th align="right">Custo total</Th>
          <Th align="right">Cotação</Th>
          <Th align="right">Valor atual</Th>
          <Th align="right">L/P</Th>
          <Th align="right">Proventos</Th>
          <Th align="right">% carteira</Th>
          <Th align="right" />
        </tr>
      </thead>
      <tbody>
        {sorted.map((position) => {
          const value = position.marketValue ?? position.totalCost;
          const share = totalMarketValue > 0 ? (value / totalMarketValue) * 100 : 0;
          const assetEarnings = earningsByAsset.get(position.asset.id) ?? 0;
          return (
            <tr key={position.asset.id} className="group">
              <Td className="max-w-[9rem]">
                <div className="flex flex-col gap-0.5">
                  <span
                    className="truncate font-medium text-foreground"
                    title={position.asset.name || position.asset.ticker}
                  >
                    {position.asset.ticker}
                  </span>
                  <TypeBadge asset={position.asset} />
                </div>
              </Td>
              <Td align="right">{formatQuantity(position.quantity)}</Td>
              <Td align="right">R$ {formatCurrency(position.averageCost)}</Td>
              <Td align="right">R$ {formatCurrency(position.totalCost)}</Td>
              <Td align="right">
                {position.currentPrice !== null ? (
                  `R$ ${formatCurrency(position.currentPrice)}`
                ) : (
                  <span className="text-xs text-muted-foreground">sem cotação</span>
                )}
              </Td>
              <Td align="right" className="font-medium">
                R$ {formatCurrency(value)}
              </Td>
              <Td align="right">
                {position.unrealizedGain !== null ? (
                  <div className="flex flex-col items-end">
                    <Money value={position.unrealizedGain} signed />
                    <span
                      className={`text-xs ${
                        position.unrealizedGain >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {position.unrealizedGainPct! >= 0 ? '+' : ''}
                      {position.unrealizedGainPct!.toFixed(2)}%
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </Td>
              <Td align="right">
                {assetEarnings > 0 ? (
                  <span className="text-green-600">R$ {formatCurrency(assetEarnings)}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </Td>
              <Td align="right">{share.toFixed(1)}%</Td>
              <Td align="right">
                <RowActions
                  onEdit={() => onEditAsset(position.asset)}
                  onDelete={() => onDeleteAsset(position.asset)}
                />
              </Td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr className="font-medium">
          <Td>Total</Td>
          <Td />
          <Td />
          <Td align="right">R$ {formatCurrency(totals.cost)}</Td>
          <Td />
          <Td align="right">R$ {formatCurrency(totals.market)}</Td>
          <Td align="right">
            <Money value={totals.market - totals.cost} signed />
          </Td>
          <Td align="right" className="text-green-600">
            R$ {formatCurrency(totals.earnings)}
          </Td>
          <Td align="right">100%</Td>
          <Td />
        </tr>
      </tfoot>
    </TableShell>
  );
}

// ============================================
// OPERAÇÕES
// ============================================

export interface TradesTableProps {
  trades: Trade[];
  assets: InvestmentAsset[];
  /** Resultado apurado por venda, vindo do motor de cálculo. */
  resultByTradeId: Map<string, number>;
  onEdit: (trade: Trade) => void;
  onDelete: (trade: Trade) => void;
}

export function TradesTable({
  trades,
  assets,
  resultByTradeId,
  onEdit,
  onDelete,
}: TradesTableProps) {
  if (trades.length === 0) {
    return <EmptyState>Nenhuma operação registrada.</EmptyState>;
  }

  const assetById = new Map(assets.map((a) => [a.id, a]));
  const sorted = [...trades].sort((a, b) => b.date.getTime() - a.date.getTime());

  return (
    <TableShell>
      <thead>
        <tr>
          <Th>Data</Th>
          <Th>Papel</Th>
          <Th>Operação</Th>
          <Th align="right">Qtd</Th>
          <Th align="right">Valor unit.</Th>
          <Th align="right">Taxas</Th>
          <Th align="right">Total</Th>
          <Th align="right">Resultado</Th>
          <Th align="right" />
        </tr>
      </thead>
      <tbody>
        {sorted.map((trade) => {
          const asset = assetById.get(trade.assetId);
          const gross = trade.quantity * trade.unitPrice;
          const total = trade.operation === 'buy' ? gross + trade.fees : gross - trade.fees;
          const result = resultByTradeId.get(trade.id);
          const isBuy = trade.operation === 'buy';
          return (
            <tr key={trade.id}>
              <Td>{trade.date.toLocaleDateString('pt-BR')}</Td>
              <Td className="max-w-[9rem] truncate font-medium text-foreground" title={asset?.name}>
                {asset?.ticker ?? '—'}
              </Td>
              <Td>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    isBuy ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  {isBuy ? (
                    <ArrowDownRight className="size-3" />
                  ) : (
                    <ArrowUpRight className="size-3" />
                  )}
                  {isBuy ? 'Compra' : 'Venda'}
                </span>
              </Td>
              <Td align="right">{formatQuantity(trade.quantity)}</Td>
              <Td align="right">R$ {formatCurrency(trade.unitPrice)}</Td>
              <Td align="right">
                {trade.fees > 0 ? (
                  `R$ ${formatCurrency(trade.fees)}`
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </Td>
              <Td align="right" className="font-medium">
                R$ {formatCurrency(total)}
              </Td>
              <Td align="right">
                {result !== undefined ? (
                  <Money value={result} signed />
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </Td>
              <Td align="right">
                <RowActions onEdit={() => onEdit(trade)} onDelete={() => onDelete(trade)} />
              </Td>
            </tr>
          );
        })}
      </tbody>
    </TableShell>
  );
}

// ============================================
// PROVENTOS
// ============================================

export interface EarningsTableProps {
  earnings: Earning[];
  assets: InvestmentAsset[];
  onEdit: (earning: Earning) => void;
  onDelete: (earning: Earning) => void;
}

export function EarningsTable({ earnings, assets, onEdit, onDelete }: EarningsTableProps) {
  if (earnings.length === 0) {
    return <EmptyState>Nenhum provento registrado.</EmptyState>;
  }

  const assetById = new Map(assets.map((a) => [a.id, a]));
  const sorted = [...earnings].sort(
    (a, b) => b.paymentDate.getTime() - a.paymentDate.getTime()
  );

  const totalNet = sorted.reduce((sum, e) => sum + (e.grossValue - e.irrfWithheld), 0);

  return (
    <TableShell>
      <thead>
        <tr>
          <Th>Pagamento</Th>
          <Th>Papel</Th>
          <Th>Tipo</Th>
          <Th align="right">Qtd base</Th>
          <Th align="right">Por cota</Th>
          <Th align="right">Bruto</Th>
          <Th align="right">IRRF</Th>
          <Th align="right">Líquido</Th>
          <Th align="right" />
        </tr>
      </thead>
      <tbody>
        {sorted.map((earning) => {
          const asset = assetById.get(earning.assetId);
          const meta = earningKinds[earning.kind];
          const reducesCost = meta?.treatment === 'custo';
          return (
            <tr key={earning.id}>
              <Td>{earning.paymentDate.toLocaleDateString('pt-BR')}</Td>
              <Td className="max-w-[9rem] truncate font-medium text-foreground" title={asset?.name}>
                {asset?.ticker ?? '—'}
              </Td>
              <Td>
                <span className="text-xs text-foreground-subtle">{meta?.label}</span>
                {reducesCost && (
                  <span className="ml-1 text-xs text-muted-foreground">(reduz custo)</span>
                )}
              </Td>
              <Td align="right">
                {earning.quantityBase ? (
                  formatQuantity(earning.quantityBase)
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </Td>
              <Td align="right">
                {earning.valuePerShare ? (
                  `R$ ${formatCurrency(earning.valuePerShare)}`
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </Td>
              <Td align="right">R$ {formatCurrency(earning.grossValue)}</Td>
              <Td align="right">
                {earning.irrfWithheld > 0 ? (
                  <span className="text-red-600">
                    -R$ {formatCurrency(earning.irrfWithheld)}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </Td>
              <Td align="right" className="font-medium">
                R$ {formatCurrency(earning.grossValue - earning.irrfWithheld)}
              </Td>
              <Td align="right">
                <RowActions
                  onEdit={() => onEdit(earning)}
                  onDelete={() => onDelete(earning)}
                />
              </Td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr className="font-medium">
          <Td colSpan={7}>Total recebido (inclui amortizações)</Td>
          <Td align="right" className="text-green-600">
            R$ {formatCurrency(totalNet)}
          </Td>
          <Td />
        </tr>
      </tfoot>
    </TableShell>
  );
}
